import type { ShapeModification } from "../types";

type ImagePayloadShape = Pick<
  ShapeModification,
  "imageDataUri" | "imageBase64" | "imageUrl" | "brandIconId"
>;
type ParsedImageDataUri = { mimeType: string; base64: string };

const IMAGE_DATA_URI_BASE64_PATTERN = /^data:([^;,]+);base64,([\s\S]+)$/i;

function hasNonEmptyString(value: unknown): boolean {
  return typeof value === "string" && value.trim().length > 0;
}

export function normalizeBase64Payload(rawPayload: string | undefined): string | null {
  if (typeof rawPayload !== "string") return null;
  const compact = rawPayload.replace(/\s+/g, "").trim();
  return compact.length > 0 ? compact : null;
}

export function parseBase64DataUri(rawDataUri: string | undefined): ParsedImageDataUri | null {
  if (typeof rawDataUri !== "string") return null;
  const trimmed = rawDataUri.trim();
  const match = trimmed.match(IMAGE_DATA_URI_BASE64_PATTERN);
  if (!match) return null;
  const base64 = normalizeBase64Payload(match[2]);
  if (!base64) return null;
  return { mimeType: match[1], base64 };
}

export function buildOpenAIImageUrl(
  rawPayload: string | undefined,
  fallbackMimeType = "image/png"
): string | null {
  const parsedDataUri = parseBase64DataUri(rawPayload);
  if (parsedDataUri) {
    return `data:${parsedDataUri.mimeType};base64,${parsedDataUri.base64}`;
  }

  const base64 = normalizeBase64Payload(rawPayload);
  if (!base64) return null;
  return `data:${fallbackMimeType};base64,${base64}`;
}

function hasParseableImageDataUri(value: unknown): boolean {
  if (typeof value !== "string") return false;
  return parseBase64DataUri(value) !== null;
}

export function hasAnyImagePayload(shape: ImagePayloadShape): boolean {
  return (
    hasNonEmptyString(shape.imageDataUri) ||
    hasNonEmptyString(shape.imageBase64) ||
    hasNonEmptyString(shape.imageUrl) ||
    hasNonEmptyString(shape.brandIconId)
  );
}

export function hasInlineImagePayload(shape: ImagePayloadShape): boolean {
  return hasParseableImageDataUri(shape.imageDataUri) || normalizeBase64Payload(shape.imageBase64) !== null;
}
